import { useMemo } from 'react';
import {
  X,
  Building2,
  DoorOpen,
  BedDouble,
  Activity,
  Layers,
  Stethoscope,
  Baby,
  HeartPulse,
  ShieldAlert,
  Hotel,
  Loader2,
  CheckCircle2,
  Clock,
  Wrench,
  Edit2,
  MapPin,
  Hash,
  AlertCircle,
} from 'lucide-react';
import { useWardBeds } from '../../services/httpServices/queries';

export interface WardDetails {
  id: string;
  wardCode: string;
  wardName: string;
  wardType: string;
  rooms?: {
    id: string;
    name: string;
    floor?: { id: string; name: string; floorNumber?: number } | null;
  }[];
  floor?: string;
  totalBeds: number;
  isActive: boolean;
}

interface WardDetailsModalProps {
  ward: WardDetails | null;
  onClose: () => void;
  onEdit?: (ward: WardDetails) => void;
}

const wardTypeConfig: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  GENERAL: { label: 'General', icon: Hotel, color: 'bg-blue-100 text-blue-700' },
  SEMI_PRIVATE: { label: 'Semi Private', icon: Layers, color: 'bg-indigo-100 text-indigo-700' },
  PRIVATE: { label: 'Private', icon: Building2, color: 'bg-emerald-100 text-emerald-700' },
  ICU: { label: 'ICU', icon: Stethoscope, color: 'bg-red-100 text-red-700' },
  NICU: { label: 'NICU', icon: Baby, color: 'bg-pink-100 text-pink-700' },
  CCU: { label: 'CCU', icon: HeartPulse, color: 'bg-rose-100 text-rose-700' },
  HDU: { label: 'HDU', icon: ShieldAlert, color: 'bg-amber-100 text-amber-700' },
};

const bedStatusConfig: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  AVAILABLE: { label: 'Available', icon: CheckCircle2, color: 'bg-success/10 text-success' },
  OCCUPIED: { label: 'Occupied', icon: BedDouble, color: 'bg-danger/10 text-danger' },
  RESERVED: { label: 'Reserved', icon: Clock, color: 'bg-warning/10 text-warning' },
  MAINTENANCE: { label: 'Maintenance', icon: Wrench, color: 'bg-text-muted/10 text-text-muted' },
};

function InfoTile({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: React.ReactNode }) {
  return (
    <div className="p-3 rounded-xl bg-background border border-border">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-text-muted mb-1">
        <Icon size={12} />
        {label}
      </div>
      <p className="text-sm font-medium text-text break-words">{value}</p>
    </div>
  );
}

export function WardDetailsModal({ ward, onClose, onEdit }: WardDetailsModalProps) {
  const { data: bedsData, isLoading: bedsLoading, isError } = useWardBeds(ward?.id || '');
  const beds = useMemo<any[]>(() => bedsData?.data || [], [bedsData]);

  const summary = useMemo(() => {
    const counts: Record<string, number> = {
      AVAILABLE: 0,
      OCCUPIED: 0,
      RESERVED: 0,
      MAINTENANCE: 0,
    };
    beds.forEach((bed) => {
      if (counts[bed.status] !== undefined) counts[bed.status] += 1;
    });
    return counts;
  }, [beds]);

  if (!ward) return null;

  const typeConfig = wardTypeConfig[ward.wardType] || {
    label: ward.wardType?.replace('_', ' ') || 'Ward',
    icon: Building2,
    color: 'bg-background text-text-muted',
  };
  const TypeIcon = typeConfig.icon;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-4 border-b border-border">
          <div className="flex items-start gap-3 min-w-0">
            <div className="p-2 rounded-lg bg-primary/10 shrink-0">
              <Building2 size={20} className="text-primary" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg font-semibold text-text truncate">{ward.wardName}</h2>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${typeConfig.color}`}>
                  <TypeIcon size={12} />
                  {typeConfig.label}
                </span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${ward.isActive ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'}`}>
                  {ward.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <p className="text-xs font-mono text-text-muted mt-0.5">{ward.wardCode || '—'}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-background text-text-muted shrink-0"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-5 overflow-y-auto">
          {/* Ward details */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <InfoTile icon={Hash} label="Ward Code" value={ward.wardCode || '—'} />
            <InfoTile icon={TypeIcon} label="Ward Type" value={typeConfig.label} />
            <InfoTile icon={Activity} label="Status" value={ward.isActive ? 'Active' : 'Inactive'} />
            <InfoTile icon={MapPin} label="Floor" value={ward.floor || '—'} />
            <InfoTile icon={DoorOpen} label="Rooms" value={ward.rooms?.length ?? 0} />
            <InfoTile icon={BedDouble} label="Capacity" value={`${ward.totalBeds ?? 0} beds`} />
          </div>

          {/* Rooms */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <DoorOpen size={14} className="text-primary" />
              <h3 className="text-sm font-semibold text-text">Rooms</h3>
            </div>
            {ward.rooms && ward.rooms.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {ward.rooms.map((room) => (
                  <span
                    key={room.id}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-background border border-border text-xs text-text"
                  >
                    <DoorOpen size={12} className="text-text-muted" />
                    {room.name}
                    {room.floor?.name && <span className="text-text-muted">· {room.floor.name}</span>}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-text-muted">No rooms linked to this ward.</p>
            )}
          </div>

          {/* Occupancy */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BedDouble size={14} className="text-primary" />
                <h3 className="text-sm font-semibold text-text">Bed Occupancy</h3>
              </div>
              {!bedsLoading && !isError && (
                <span className="text-xs text-text-muted">{beds.length} bed{beds.length === 1 ? '' : 's'} registered</span>
              )}
            </div>

            {bedsLoading ? (
              <div className="flex items-center gap-2 p-3 text-sm text-text-muted">
                <Loader2 size={14} className="animate-spin" />
                Loading beds...
              </div>
            ) : isError ? (
              <div className="flex items-center gap-2 p-3 bg-danger/10 border border-danger/20 rounded-lg text-danger text-sm">
                <AlertCircle size={14} />
                Failed to load beds for this ward.
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {(['AVAILABLE', 'OCCUPIED', 'RESERVED', 'MAINTENANCE'] as const).map((status) => {
                    const config = bedStatusConfig[status];
                    const Icon = config.icon;
                    return (
                      <div key={status} className="p-3 rounded-xl bg-background border border-border">
                        <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium ${config.color}`}>
                          <Icon size={12} />
                          {config.label}
                        </div>
                        <p className="text-lg font-semibold text-text mt-1.5">{summary[status]}</p>
                      </div>
                    );
                  })}
                </div>

                {beds.length > 0 && (
                  <div className="border border-border rounded-xl overflow-hidden">
                    <div className="max-h-64 overflow-y-auto">
                      <table className="w-full">
                        <thead className="sticky top-0 bg-background/95 backdrop-blur">
                          <tr className="border-b border-border">
                            <th className="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">Bed</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">Type</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">Price</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {beds.map((bed) => {
                            const statusConfig = bedStatusConfig[bed.status];
                            return (
                              <tr key={bed.id} className="hover:bg-background/50">
                                <td className="px-3 py-2 text-sm font-medium text-text">{bed.bedNumber}</td>
                                <td className="px-3 py-2 text-sm text-text-muted">{bed.type?.replace('_', ' ')}</td>
                                <td className="px-3 py-2 text-sm text-text">৳{Number(bed.price || 0).toFixed(2)}</td>
                                <td className="px-3 py-2">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig?.color || 'bg-text-muted/10 text-text-muted'}`}>
                                    {statusConfig?.label || bed.status}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-border text-sm font-medium text-text-muted hover:text-text hover:bg-background transition-colors"
          >
            Close
          </button>
          {onEdit && (
            <button
              onClick={() => onEdit(ward)}
              className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary-dark transition-colors flex items-center gap-2"
            >
              <Edit2 size={16} />
              Edit Ward
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
